// src/composables/useDraggableModal.js
//
// Torna um painel de modal arrastável pelo cabeçalho, sem mexer no layout que já
// o posiciona: o painel continua centralizado pelo flex do backdrop e o arrasto
// entra como um `transform: translate()` acumulado por cima.
//
// Cuidado conhecido: um ancestral com `transform` vira containing block dos
// descendentes `position: fixed`. Quem usar este composable precisa manter os
// elementos fixed (toasts, sub-modais) em <Teleport to="body">, fora do painel.
//
// A posição não persiste: o estado vive no componente, então desmontar o modal
// (v-if do pai) devolve o painel ao centro na próxima abertura. Quem alterna a
// prop `show` sem desmontar deve chamar resetPosition() ao reabrir.

import { ref, computed, onBeforeUnmount } from "vue";

export function useDraggableModal(options = {}) {
  const {
    // Abaixo desta largura o painel já ocupa quase toda a tela e o arrasto só
    // atrapalha o gesto de rolagem — desligado.
    minViewportWidth = 768,
    // Respiro mínimo entre o painel e a borda da janela.
    margin = 8,
  } = options;

  const panelRef = ref(null);
  const offset = ref({ x: 0, y: 0 });
  const isDragging = ref(false);

  const isEnabled = ref(
    typeof window === "undefined" ? false : window.innerWidth >= minViewportWidth
  );

  // Estado do arrasto em curso (não precisa ser reativo).
  let drag = null;

  const panelStyle = computed(() =>
    offset.value.x === 0 && offset.value.y === 0
      ? null
      : {
          transform: `translate(${offset.value.x}px, ${offset.value.y}px)`,
        }
  );

  // Limites do offset para o painel não sair da janela. Quando o painel é maior
  // que a viewport os limites se invertem — aí a faixa vira o inverso, para
  // continuar sendo possível arrastar até a parte que ficou de fora.
  function clampOffset(x, y, base) {
    const clamp = (v, a, b) =>
      a <= b ? Math.min(Math.max(v, a), b) : Math.min(Math.max(v, b), a);

    return {
      x: clamp(
        x,
        margin - base.left,
        window.innerWidth - margin - base.width - base.left
      ),
      y: clamp(
        y,
        margin - base.top,
        window.innerHeight - margin - base.height - base.top
      ),
    };
  }

  // Geometria do painel descontando o offset atual, ou seja, onde ele estaria
  // se nunca tivesse sido arrastado. É essa base que os limites usam.
  function readBase() {
    const rect = panelRef.value?.getBoundingClientRect();
    if (!rect) return null;
    return {
      left: rect.left - offset.value.x,
      top: rect.top - offset.value.y,
      width: rect.width,
      height: rect.height,
    };
  }

  function onPointerMove(event) {
    if (!drag) return;
    offset.value = clampOffset(
      drag.startOffset.x + (event.clientX - drag.startX),
      drag.startOffset.y + (event.clientY - drag.startY),
      drag.base
    );
  }

  function stopDragging() {
    if (!drag) return;
    drag = null;
    isDragging.value = false;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", stopDragging);
    window.removeEventListener("pointercancel", stopDragging);
  }

  // Handler do `pointerdown` no cabeçalho.
  function onHandlePointerDown(event) {
    if (!isEnabled.value || isDragging.value) return;
    // Só botão principal; e o botão de fechar (e afins) dentro do cabeçalho
    // continua sendo clique, não alça de arrasto.
    if (event.button !== 0) return;
    if (event.target.closest("button, a, input, select, [contenteditable]")) {
      return;
    }

    const base = readBase();
    if (!base) return;

    drag = {
      startX: event.clientX,
      startY: event.clientY,
      startOffset: { ...offset.value },
      base,
    };
    isDragging.value = true;

    // Evita que o arrasto vire seleção de texto do cabeçalho.
    event.preventDefault();

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", stopDragging);
    window.addEventListener("pointercancel", stopDragging);
  }

  // Encolher a janela pode jogar o painel para fora; o resize reprende o offset
  // aos novos limites (e zera ao cair para a faixa mobile, onde não há como
  // corrigir a posição na mão).
  function onWindowResize() {
    stopDragging();
    isEnabled.value = window.innerWidth >= minViewportWidth;

    if (!isEnabled.value) {
      offset.value = { x: 0, y: 0 };
      return;
    }
    if (offset.value.x === 0 && offset.value.y === 0) return;

    const base = readBase();
    if (!base) return;
    offset.value = clampOffset(offset.value.x, offset.value.y, base);
  }

  window.addEventListener("resize", onWindowResize);

  onBeforeUnmount(() => {
    stopDragging();
    window.removeEventListener("resize", onWindowResize);
  });

  // Devolve o painel ao centro. Para modais que não desmontam entre aberturas.
  function resetPosition() {
    stopDragging();
    offset.value = { x: 0, y: 0 };
  }

  return {
    panelRef,
    panelStyle,
    isDragging,
    isDraggable: isEnabled,
    onHandlePointerDown,
    resetPosition,
  };
}
